<script setup lang="ts">
import { useToast } from '@/composables/useToast'
import BaseToast from './BaseToast.vue'

const { toasts, dismiss, pauseTimer, resumeTimer } = useToast()
</script>

<template>
  <Teleport to="body">
    <!-- Accessibility announcements -->
    <div
      class="sr-only"
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      <span v-for="toast in toasts" :key="toast.id + '-sr'">
        {{ toast.title ? `${toast.title}: ` : '' }}{{ toast.message }}
      </span>
    </div>

    <!-- Toast stack -->
    <div
      class="fixed top-5 right-5 z-[100] flex flex-col gap-3 pointer-events-none max-h-[calc(100vh-2.5rem)] overflow-hidden"
      aria-label="Notifications"
    >
      <TransitionGroup
        enter-active-class="toast-enter-active"
        enter-from-class="toast-enter-from"
        enter-to-class="toast-enter-to"
        leave-active-class="toast-leave-active"
        leave-from-class="toast-leave-from"
        leave-to-class="toast-leave-to"
        move-class="toast-move"
      >
        <div
          v-for="toast in toasts"
          :key="toast.id"
          class="pointer-events-auto transform-gpu"
        >
          <BaseToast
            :type="toast.type"
            :title="toast.title"
            :message="toast.message"
            :dismissible="toast.dismissible"
            :show-progress="toast.showProgress"
            :duration="toast.duration"
            :created-at="toast.createdAt"
            :actions="toast.actions"
            @dismiss="dismiss(toast.id)"
            @pause="pauseTimer(toast.id)"
            @resume="resumeTimer(toast.id)"
          />
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<style scoped>
.toast-enter-active {
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.toast-enter-from {
  opacity: 0;
  transform: translateX(40px) scale(0.96);
  filter: blur(4px);
}

.toast-enter-to {
  opacity: 1;
  transform: translateX(0) scale(1);
  filter: blur(0);
}

.toast-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 1, 1);
}

.toast-leave-from {
  opacity: 1;
  transform: translateX(0) scale(1);
}

.toast-leave-to {
  opacity: 0;
  transform: translateX(30px) scale(0.95);
  filter: blur(2px);
}

.toast-move {
  transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}
</style>
